import dayjs from "dayjs";
import { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useFileDownloadMutation } from "../../../../../../api/services/client/clientApis";
import {
  SearchDocumentListDropdownFilterKey,
  SearchDocumentResultRow,
} from "../../../../../../api/services/client/models";
import {
  Column,
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiDateFieldFormController,
  MuiGrid,
  MuiLink,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
  StatusBadge,
  TableHeaderSearchIcon,
  WarningConfirmationModal,
} from "../../../../../../components";
import MuiEllipseLabel from "../../../../../../components/ellipseLabel/MuiEllipseLabel";
import {
  CLIENT_DOCUMENT_TYPES,
  DATE_FORMAT,
  EMPTY_VALUE_DASH,
  MAX_LENGTH_50,
  PAGE_IDS,
} from "../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../../helpers/types/common";
import {
  convertToUserFormat,
  createOptionsFromSet,
  filterListByFilterKeyValue,
  getValueOrBlank,
  isNotEmpty,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../../redux/slices/appState/appStateSelector";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { isDocumentTypeContract } from "../../../../helper";
import { InheritDocumentsResultFormKey } from "../../../../modelsAndSchemas/Models";
import { inheritDocumentSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { InheritDocumentsResultWrapper } from "./InheritDocumentsResult.style";

const pageId = PAGE_IDS.INHERIT_DOCUMENTS_SEARCH_RESULT;

interface InheritDocumentsResultProps {
  rows: SearchDocumentResultRow[];
  isSearch: boolean;
  onRowSelected?: (row: RowProps) => void;
  setSelectedDocuments: (documents: SearchDocumentResultRow[]) => void;
}

export const InheritDocumentsResult: FC<InheritDocumentsResultProps> = ({
  rows,
  isSearch,
  onRowSelected,
  setSelectedDocuments,
}) => {
  const { commonARIALabel } = useNavigationSiteCore();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { useFetchLabel, commonLabels, fetchValueByKeyOfDropdown } =
    useSiteCore();
  const dispatch = useDispatch();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const tableRef = useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [
    showRedirectionConfirmationModal,
    setShowRedirectionConfirmationModal,
  ] = useState(false);
  const documentItemRef = useRef<SearchDocumentResultRow | null>(null);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    SearchDocumentResultRow[]
  >([]);
  const [uniqueFiltersOptionObj, setUniqueFiltersOptionObj] = useState<{
    [K in SearchDocumentListDropdownFilterKey]: LabelValue[];
  }>({
    [InheritDocumentsResultFormKey.documentStatus]: [],
  });

  const {
    mutate: downloadFile,
    isSuccess: isSuccessOnDownload,
    isPending: isFileDownloadPending,
    data: dataOnDownload,
  } = useFileDownloadMutation();

  useEffect(() => {
    dispatch(
      toggleLoader({
        isLoadingDocuments: {
          isVisible: isFileDownloadPending,
          message: commonLabels.loadingPleaseWait,
        },
      }),
    );
    return () => {
      toggleLoader({
        isLoadingDocuments: {
          isVisible: false,
        },
      });
    };
  }, [isFileDownloadPending]);

  const previewDownloadedFile = (file: Blob) => {
    const fileURL = URL.createObjectURL(file);
    // Open file in a new tab
    window.open(fileURL, "_blank");
    // Optionally, revoke the URL later to free memory
    setTimeout(() => {
      URL.revokeObjectURL(fileURL);
    }, 10000);
  };

  useEffect(() => {
    if (isSuccessOnDownload && dataOnDownload && documentItemRef.current) {
      const { document } = documentItemRef.current;
      const file = new File([dataOnDownload.blob], document.name, {
        type: dataOnDownload.blob.type,
      });
      documentItemRef.current = null;
      previewDownloadedFile(file);
    }
  }, [isSuccessOnDownload, dataOnDownload]);

  const { control, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [InheritDocumentsResultFormKey.clientName]: "",
      [InheritDocumentsResultFormKey.clientCode]: "",
      [InheritDocumentsResultFormKey.documentName]: "",
      [InheritDocumentsResultFormKey.contractId]: "",
      [InheritDocumentsResultFormKey.documentType]: "",
      [InheritDocumentsResultFormKey.documentStatus]: "",
      [InheritDocumentsResultFormKey.effectiveDate]: "",
      [InheritDocumentsResultFormKey.expirationDate]: "",
    },
  });
  const tableColumnFilterWatch = useWatch({ control });

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const {
    inheritDocumentTitle,
    resultSectionTitle,
    clientName,
    clientCode,
    documentName,
    contractID,
    documentType,
    documentStatus,
    effectiveDate,
    expirationDate,
    documentTypeList,
    documentStatusList,
    redirectToNewTabConfirmationMessage,
    proceedButton,
    stayHereButton,
  } = inheritDocumentSiteCoreKeys;

  const getLabel = useFetchLabel(inheritDocumentTitle);

  const getValueByKeyOfDropdown = (listKey: string, key: string) =>
    fetchValueByKeyOfDropdown(inheritDocumentTitle, listKey, key);

  useEffect(() => {
    if (isNotEmpty(allUpdatedRows)) {
      const filterEffectiveDate = tableColumnFilterWatch.effectiveDate
        ? dayjs(
            tableColumnFilterWatch.effectiveDate,
            userCountryDateFormat,
          ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN)
        : "";
      const filterExpirationDate = tableColumnFilterWatch.expirationDate
        ? dayjs(
            tableColumnFilterWatch.expirationDate,
            userCountryDateFormat,
          ).format(DATE_FORMAT.YYYY_MM_DD_HYPHEN)
        : "";
      const filterList = filterListByFilterKeyValue(allUpdatedRows, {
        ...tableColumnFilterWatch,
        effectiveDate: filterEffectiveDate,
        expirationDate: filterExpirationDate,
      }) as SearchDocumentResultRow[];
      setFilteredResults([...filterList]);
      resetPagination();
      setAllCheckboxSelected(filterList);
    }
  }, [tableColumnFilterWatch]);

  const setAllCheckboxSelected = (updatedRows: SearchDocumentResultRow[]) => {
    // Check if all rows have isChecked as true
    const allSelected = updatedRows.every(
      (row: SearchDocumentResultRow) => row.isChecked === true,
    );
    setIsAllSelected(updatedRows.length ? allSelected : false);
  };

  const getUniqueFiltersOptions = (rows: SearchDocumentResultRow[]) => {
    const documentStatusSet = new Set<string>();
    const documentTypeSet = new Set<string>();

    rows.forEach(({ statusId, documentType }) => {
      statusId && documentStatusSet.add(statusId);
      documentType && documentTypeSet.add(documentType);
    });

    const documentStatusArray = createOptionsFromSet(
      documentStatusSet,
      (status) => getValueByKeyOfDropdown(documentStatusList, status) || status,
    );

    const documentTypeArray = createOptionsFromSet(
      documentTypeSet,
      (type) => getValueByKeyOfDropdown(documentTypeList, type) || type,
    );

    return {
      documentStatusArray,
      documentTypeArray,
    };
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const { documentStatusArray, documentTypeArray } =
        getUniqueFiltersOptions(rows);
      setUniqueFiltersOptionObj({
        [InheritDocumentsResultFormKey.documentStatus]: documentStatusArray,
        [InheritDocumentsResultFormKey.documentType]: documentTypeArray,
      });

      // this is to check if document is already isInherited & show checkbox as disabled-checked using key isInherited from API
      const mappedResults = rows.map((item: SearchDocumentResultRow) => {
        return {
          ...item,
          documentStatusLabel: getValueByKeyOfDropdown(
            documentStatusList,
            item.statusId,
          ),
          id: item.document.id,
          documentName: item.document.name,
          mappedContractId: isDocumentTypeContract(item?.documentType)
            ? `${item.contractId}`
            : EMPTY_VALUE_DASH,
          isChecked: item.isInherited,
          isDisabled: item.isInherited,
        };
      });

      setAllUpdatedRows(mappedResults);
      setFilteredResults([...mappedResults]);
      setIsAllSelected(false);
      reset();
      resetPagination();
    }
  }, [rows, siteCoreLoading]);

  const updateSelectedDataToParent = (
    filteredResults: SearchDocumentResultRow[],
  ) => {
    // update selected participants in parent component
    setSelectedDocuments(filteredResults);
  };

  const evaluateIsChecked = (checked: boolean, isDisabled?: boolean) => {
    let isChecked = checked;

    if (isDisabled) {
      isChecked = true;
    }

    return isChecked;
  };

  const onHeaderCheckboxChange = (checked: boolean) => {
    setIsAllSelected(checked);
    const updatedRowsWithCheckedStatus = filteredResults.map(
      (row: SearchDocumentResultRow) => ({
        ...row,
        isChecked: evaluateIsChecked(checked, row?.isDisabled),
      }),
    );

    setFilteredResults([...updatedRowsWithCheckedStatus]);

    // Also update `allUpdatedRows` to reflect changes for the whole dataset
    setAllUpdatedRows([...updatedRowsWithCheckedStatus]);

    // Update parent component state
    updateSelectedDataToParent([...updatedRowsWithCheckedStatus]);
  };

  const onItemCheckboxChange = (checked: boolean, itemId: string) => {
    const updatedRows = filteredResults.map((row: SearchDocumentResultRow) =>
      row.id === itemId
        ? {
            ...row,
            isChecked: evaluateIsChecked(checked, row?.isDisabled),
          }
        : row,
    );
    setFilteredResults([...updatedRows]);
    setAllCheckboxSelected(updatedRows);
    setAllUpdatedRows([...updatedRows]);

    // Update parent component state
    updateSelectedDataToParent([...updatedRows]);
  };

  const handleDocumentNameViewClick = (row: SearchDocumentResultRow) => {
    documentItemRef.current = row;
    setShowRedirectionConfirmationModal(true);
  };

  const handleRedirectionConfirmationModalClose = () => {
    documentItemRef.current = null;
    setShowRedirectionConfirmationModal(false);
  };

  const handleRedirectionConfirmationModalPrimaryButtonClick = () => {
    if (documentItemRef.current && isNotEmpty(documentItemRef.current)) {
      // This documentId will be used for downloading the blob data of the document
      const { documentType, documentId } = documentItemRef.current;
      downloadFile({
        documentId: documentId,
        documentType: isDocumentTypeContract(documentType)
          ? CLIENT_DOCUMENT_TYPES.clientContract
          : CLIENT_DOCUMENT_TYPES.clientOthers,
      });
      setShowRedirectionConfirmationModal(false);
    }
  };

  const columns: Column[] = [
    {
      id: InheritDocumentsResultFormKey.isChecked,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel?.selectAll}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) =>
            onHeaderCheckboxChange(checked)
          }
        />
      ),
      render: (row: RowProps) => {
        return (
          <MuiCheckbox
            disabled={row?.isInherited || row?.isDisabled}
            checked={row.isChecked ?? false}
            hideLabel={true}
            checkboxLabel={replaceHashWithValue(
              commonARIALabel.selectRowItemName,
              `${row.name}`,
            )}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              onItemCheckboxChange(checked, row.id as string);
            }}
          />
        );
      },
    },
    {
      id: InheritDocumentsResultFormKey.clientName,
      isSortVisible: true,
      label: getLabel(clientName),
      isEmptyValue: true,
    },
    {
      id: InheritDocumentsResultFormKey.clientCode,
      isSortVisible: true,
      label: getLabel(clientCode),
      isEmptyValue: true,
    },
    {
      id: InheritDocumentsResultFormKey.documentName,
      isSortVisible: true,
      label: getLabel(documentName),
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiLink
          onClick={() =>
            handleDocumentNameViewClick(row as SearchDocumentResultRow)
          }
        >
          <MuiEllipseLabel
            labelTypoClassName="Link document-name-link"
            text={row.documentName}
            labelTypoVariant="h3"
            placement="bottom-start"
            pointerCursor
          />
        </MuiLink>
      ),
    },
    {
      id: InheritDocumentsResultFormKey.contractId,
      isSortVisible: true,
      label: getLabel(contractID),
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row?.mappedContractId}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: InheritDocumentsResultFormKey.documentType,
      isSortVisible: true,
      label: getLabel(documentType),
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {getValueByKeyOfDropdown(documentTypeList, row?.documentType)}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: InheritDocumentsResultFormKey.documentStatus,
      isSortVisible: true,
      isEmptyValue: true,
      label: getLabel(documentStatus),
      render: (row: RowProps) => (
        <StatusBadge label={row.documentStatusLabel} status={row.statusId} />
      ),
    },
    {
      id: InheritDocumentsResultFormKey.effectiveDate,
      isSortVisible: true,
      label: getLabel(effectiveDate),
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.effectiveDate)
              ? convertToUserFormat(row.effectiveDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: InheritDocumentsResultFormKey.expirationDate,
      isSortVisible: true,
      label: getLabel(expirationDate),
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {isNotEmpty(row.expirationDate)
              ? convertToUserFormat(row.expirationDate, userCountryDateFormat)
              : EMPTY_VALUE_DASH}
          </MuiTypography>
        </MuiBox>
      ),
    },
  ];

  const renderFilterTextField = (name: string) => {
    return (
      <FormController key={name} control={control} name={name}>
        <MuiTextField
          key={name}
          id={`${pageId}-${name}`}
          aria-label={commonLabels.search}
          startAdornment={<TableHeaderSearchIcon />}
          data-testid="company-input"
          showCloseIcon={true}
          placeholder={commonLabels.search}
          maxLength={MAX_LENGTH_50}
          requiredARIALabel={commonARIALabel.required}
          shouldTrimSpaces={false}
        />
      </FormController>
    );
  };

  const renderFilterDropdown = (name: string) => {
    const options = uniqueFiltersOptionObj[name];
    return (
      <FormController key={name} control={control} name={name}>
        <MuiSelect
          id={`${pageId}-${name}`}
          value=""
          label=""
          options={options}
          visibleDefaultSelectOption={true}
          requiredARIALabel={commonARIALabel.required}
          placeholder={commonLabels.selectPlaceHolder}
        />
      </FormController>
    );
  };

  const tableHeaderForm = [
    <></>,
    renderFilterTextField(InheritDocumentsResultFormKey.clientName),
    renderFilterTextField(InheritDocumentsResultFormKey.clientCode),
    renderFilterTextField(InheritDocumentsResultFormKey.documentName),
    renderFilterTextField(InheritDocumentsResultFormKey.contractId),
    renderFilterDropdown(InheritDocumentsResultFormKey.documentType),
    renderFilterDropdown(InheritDocumentsResultFormKey.documentStatus),
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${InheritDocumentsResultFormKey.effectiveDate}`}
      id={`${pageId}-${InheritDocumentsResultFormKey.effectiveDate}`}
      name={InheritDocumentsResultFormKey.effectiveDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(
        getValues(InheritDocumentsResultFormKey.effectiveDate),
      )}
    />,
    <MuiDateFieldFormController
      control={control}
      id={`${pageId}-${InheritDocumentsResultFormKey.expirationDate}`}
      key={`${pageId}-${InheritDocumentsResultFormKey.expirationDate}`}
      name={InheritDocumentsResultFormKey.expirationDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
      clearable
      hideCalendarIconOnValue={isNotEmpty(
        getValues(InheritDocumentsResultFormKey.expirationDate),
      )}
    />,
  ];

  return (
    <InheritDocumentsResultWrapper>
      <MuiGrid container data-testid="client-result">
        <MuiGrid size={{ xs: 12 }} className="form-header">
          <MuiTypography variant="h2SemiBold" className="form-header-left">
            {getLabel(resultSectionTitle)}
          </MuiTypography>
          {rows.length > 0 && (
            <MuiTypography variant="h3SemiBold" className="selected-count">
              {filteredResults.length}
            </MuiTypography>
          )}
        </MuiGrid>
        <MuiGrid container>
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...filteredResults]}
              noDataInApi={rows.length === 0 && !isSearch}
              onRowSelected={onRowSelected}
              emptyMessage={commonLabels.applyNecessaryFiltersMessage}
              noResultMessage={commonLabels.noResult}
              priorityResults={[]}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiGrid>
      {showRedirectionConfirmationModal && (
        <WarningConfirmationModal
          labelContent={getLabel(redirectToNewTabConfirmationMessage)}
          modalTitle={commonLabels.confirmation}
          show={showRedirectionConfirmationModal}
          onClose={handleRedirectionConfirmationModalClose}
          onPrimaryButtonClick={
            handleRedirectionConfirmationModalPrimaryButtonClick
          }
          primaryButtonTitle={getLabel(proceedButton)}
          secondaryButtonTitle={getLabel(stayHereButton)}
          onSecondaryButtonClick={handleRedirectionConfirmationModalClose}
        />
      )}
    </InheritDocumentsResultWrapper>
  );
};
